import { AfterViewInit, Component, forwardRef, Input, Renderer2, OnInit } from '@angular/core';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import * as dayjs from 'dayjs';
import { OverlayService } from '@services/overlay.service';
import { BaseFormControlComponent } from '../base-form-control';
import { FormControlConstants } from '../../../constants/form-control.constants';

export interface DateControlPeriodParams {
  startDate: string;
  endDate: string;
}

@Component({
  selector: 'aux-input',
  templateUrl: './input.component.html',
  styles: [
    `
      :host {
        display: block;
      }

      ::-webkit-input-placeholder {
        font-style: italic;
      }

      :-moz-placeholder {
        font-style: italic;
      }

      ::-moz-placeholder {
        font-style: italic;
      }

      /* remove arrows when number input */
      ::ng-deep input::-webkit-outer-spin-button,
      input::-webkit-inner-spin-button,
      input[type='number'] {
        -webkit-appearance: none;
        -moz-appearance: textfield;
        margin: 0;
      }

      :-ms-input-placeholder {
        font-style: italic;
      }
    `,
  ],
  styleUrls: ['input.component.css'],
  // changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
  ],
})
export class InputComponent extends BaseFormControlComponent implements AfterViewInit, OnInit {
  readonly formControlConstants = FormControlConstants;

  @Input() type = 'text';

  @Input() autocomplete = 'on';

  @Input() textArea = false;

  @Input() rows = 3;

  @Input() icon = '';

  @Input() showRequiredAsterisk = false;

  @Input() autofocus = false;

  @Input() min = '';

  @Input() max = '';

  @Input() trialTimelinePeriod?: DateControlPeriodParams;

  @Input() disabled = false;

  @Input() noTriggerValueChangeOnBlur?: boolean;

  @Input() inputClassName = '';

  constructor(private renderer: Renderer2, private overlayService: OverlayService) {
    super();
  }

  ngOnInit() {
    if (this.min === '' && (this.type === 'date' || this.type === 'month')) {
      this.min = this.formControlConstants.MIN_VALUE.DATE;
    }
  }

  ngAfterViewInit() {
    // This solution is implemented in this way to focus element inside ag-grid cell
    if (this.autofocus) {
      setTimeout(() => {
        const element = this.renderer.selectRootElement(`#${this.uid}`);
        element.focus();
      });
    }
  }

  handleMaxDate() {
    if (this.type !== 'date' && this.type !== 'month') {
      return;
    }

    if (!dayjs(this.fc.value).isValid() && this.fc.value !== null) {
      this.fc.setValue(null);

      return;
    }

    if (this.fc.errors?.trialTimelinePeriod) {
      return;
    }

    if (
      this.trialTimelinePeriod?.endDate &&
      dayjs(this.fc.value).isAfter(this.trialTimelinePeriod?.endDate)
    ) {
      this.fc.setErrors({ trialTimelinePeriod: true });
      return;
    }

    if (dayjs(this.fc.value).isAfter(this.max)) {
      if (this.autofocus) {
        this.onChange(undefined);
        this.overlayService.error(this.formControlConstants.VALIDATION_MESSAGE.MONTH_MAX);
      } else {
        this.fc.setValue(null);
        this.fc.setErrors({ month_max: true });
      }
    }
  }

  handleMinDate() {
    if (this.type !== 'date' && this.type !== 'month') {
      return;
    }

    if (!dayjs(this.fc.value).isValid() && this.fc.value !== null) {
      this.fc.setValue(null);

      return;
    }

    if (
      this.trialTimelinePeriod?.startDate &&
      dayjs(this.fc.value).isBefore(this.trialTimelinePeriod?.startDate)
    ) {
      this.fc.setErrors({ trialTimelinePeriod: true });
      return;
    }

    if (dayjs(this.fc.value).isBefore(this.min)) {
      if (this.autofocus) {
        this.onChange(undefined);
        if (this.type === 'date') {
          this.overlayService.error(this.formControlConstants.VALIDATION_MESSAGE.DATE);
        } else {
          this.overlayService.error(this.formControlConstants.VALIDATION_MESSAGE.MONTH_MIN);
        }
      } else {
        this.fc.setValue(null);
        if (this.type === 'date') {
          this.fc.setErrors({ date: true });
        } else {
          this.fc.setErrors({ month_min: true });
        }
      }
    }
  }

  onKeyDown({ key }: KeyboardEvent) {
    if (key === 'Enter' || (key === 'Tab' && this.autofocus)) {
      this.handleMinDate();
      this.handleMaxDate();
    }
  }

  onBlur() {
    if (this.type === 'date') {
      if (this.fc.value) {
        this.fc.setValue(this.fc.value.trim());
      }
      this.handleMinDate();
      this.handleMaxDate();

      if (!this.noTriggerValueChangeOnBlur && !this.fc.errors) {
        super.onBlur();
      }
    } else {
      super.onBlur();
    }
  }
}
